import React, { useState, useRef } from "react";
import { API_URL } from "../config";
import "./AudioRecorder.css";

const AudioRecorder = ({ onUpload }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm;codecs=opus",
      });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        chunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        setUploadStatus("Recording complete.");
      };

      mediaRecorder.start();
      setIsRecording(true);
      setAudioBlob(null);
      setUploadStatus("");
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime((t) => t + 1);
      }, 1000);
    } catch (err) {
      console.error("Error accessing microphone:", err);
      setUploadStatus(`Error: ${err.message}`);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      streamRef.current?.getTracks().forEach((track) => track.stop());
      setIsRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const uploadAudio = async () => {
    if (!audioBlob) {
      setUploadStatus("No audio to upload");
      return;
    }

    setIsUploading(true);
    setUploadStatus("Uploading...");

    try {
      const formData = new FormData();
      formData.append("file", audioBlob, "audio.webm");

      const response = await fetch(`${API_URL}/cuppings/entries`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Upload failed");
      }

      const result = await response.json();
      setUploadStatus("✓ Processed successfully");
      setAudioBlob(null);
      if (onUpload) {
        onUpload(result);
      }
    } catch (err) {
      console.error("Upload error:", err);
      setUploadStatus(`Error: ${err.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  return (
    <div className="audio-recorder">
      {!isRecording && !audioBlob && (
        <button onClick={startRecording} className="btn-primary">
          Record Note
        </button>
      )}

      {isRecording && (
        <div className="recorder-controls">
          {/* State is handled by visual indicator below, button just stops */}
          <button onClick={stopRecording} className="btn-danger">
            Stop
          </button>
        </div>
      )}

      {audioBlob && (
        <div className="recorder-controls">
          <button
            onClick={uploadAudio}
            disabled={isUploading}
            className="btn-success"
          >
            {isUploading ? "Processing..." : "Upload & Process"}
          </button>
          {!isUploading && (
            <button
              onClick={() => setAudioBlob(null)}
              style={{
                background: "transparent",
                color: "var(--text-muted)",
                border: "none",
                fontSize: "12px",
                cursor: "pointer",
                marginLeft: "10px",
              }}
            >
              Cancel
            </button>
          )}
        </div>
      )}

      <div className="recorder-status">
        {isRecording && (
          <div className="recording-indicator">
            <span className="dot"></span> {formatTime(recordingTime)}
          </div>
        )}
        {uploadStatus && (
          <div
            className={`status-message ${
              uploadStatus.includes("Error")
                ? "error"
                : uploadStatus.includes("✓")
                ? "success"
                : ""
            }`}
          >
            {uploadStatus}
          </div>
        )}
      </div>
    </div>
  );
};

export default AudioRecorder;
